// src/pages/HomePage.js
/* global chrome */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './HomePage.css';
import appLogo from '../assets/appLogo.svg';
import SearchBar from '../components/SearchBar';
import { db } from '../firebase';
import { collection, getDocs, query as firestoreQuery } from "firebase/firestore";
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { AudioContext } from '../contexts/AudioContext';
import { trackEvent, AnalyticsEvents } from '../utils/analytics';
import { getPodcastDetails } from '../utils/apiHelpers';
import { doc, updateDoc } from 'firebase/firestore';

const HomePage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth(); // Use centralized auth context
  const { playEpisode } = useContext(AudioContext);
  const [favouritePodcasts, setFavouritePodcasts] = useState([]);
  const [loadingFavourites, setLoadingFavourites] = useState(true);
  const [continueEpisode, setContinueEpisode] = useState(null);

  useEffect(() => {
    const storageKey = `listen2_last_played_${currentUser?.uid || 'guest'}`;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setContinueEpisode(parsed);
      } catch (e) {
        console.error("Failed to parse continue episode", e);
      }
    } else {
      setContinueEpisode(null);
    }
  }, [currentUser]);

  const handleContinuePlay = () => {
    if (continueEpisode) {
      const resumeTime = Math.max(0, continueEpisode.currentTime - 5);
      playEpisode(continueEpisode.episode, resumeTime);
    }
  };

  const handleCloseContinue = () => {
    const storageKey = `listen2_last_played_${currentUser?.uid || 'guest'}`;
    localStorage.removeItem(storageKey);
    setContinueEpisode(null);
  };

  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds) || timeInSeconds === Infinity || timeInSeconds < 0) {
      return '00:00';
    }
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    } else {
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
  };

  // Effect to fetch favourites when currentUser changes (one-time fetch, not real-time)
  useEffect(() => {
    const fetchFavourites = async () => {
      if (currentUser) {
        setLoadingFavourites(true);
        try {
          const favouritesRef = collection(db, "users", currentUser.uid, "favourites");
          const q = firestoreQuery(favouritesRef);
          const querySnapshot = await getDocs(q);

          const favs = [];
          
          for (const document of querySnapshot.docs) {
            let data = document.data();
            
            // Auto-migrate legacy favourites that are missing feedUrl
            if (!data.feedUrl && !data.url) {
              try {
                const details = await getPodcastDetails(data.id);
                if (details && (details.url || details.feedUrl)) {
                  data.feedUrl = details.url || details.feedUrl;
                  // Update Firestore in the background
                  updateDoc(doc(db, "users", currentUser.uid, "favourites", document.id), {
                    feedUrl: data.feedUrl
                  }).catch(e => console.warn("Background migration failed", e));
                }
              } catch (e) {
                console.warn("Failed to fetch legacy favourite details", e);
              }
            }
            
            favs.push({ firestoreDocId: document.id, ...data });
          }

          setFavouritePodcasts(favs);
          if (window.chrome && chrome.runtime) {
            try {
              const sessionToken = await currentUser.getIdToken();
              chrome.runtime.sendMessage('efdnlegncogblfpgmhmaeibblfehpljl', {
                action: 'SYNC_FAVOURITES',
                favourites: favs,
                isAuthenticated: true,
                sessionToken: sessionToken
              });
            } catch (e) {
              console.warn("Could not sync favourites to extension", e);
            }
          }
        } catch (error) {
          console.error("Error fetching favourites:", error);
        } finally {
          setLoadingFavourites(false);
        }
      } else {
        setFavouritePodcasts([]);
        setLoadingFavourites(false);
        if (window.chrome && chrome.runtime) {
          try {
            chrome.runtime.sendMessage('efdnlegncogblfpgmhmaeibblfehpljl', {
              action: 'SYNC_FAVOURITES',
              favourites: [],
              isAuthenticated: false
            });
          } catch (e) {
            console.warn("Could not sync logout state to extension", e);
          }
        }
      }
    };

    fetchFavourites();
  }, [currentUser]);

  const handleHomePageSearch = useCallback((query) => {
    trackEvent(AnalyticsEvents.SEARCH_PERFORMED, { query }, currentUser?.uid);
    navigate(`/search-results/${encodeURIComponent(query)}`);
  }, [navigate, currentUser]);

  return (
    <>
      <Header />
      <div className="home-page-container">
        {/* Your existing appLogo */}
        <img src={appLogo} alt="Listen2 App Logo" className="app-logo" />

        {/* Your existing SearchBar container and SearchBar */}
        <div style={{ margin: '30px 0', width: '600px', display: 'flex', justifyContent: 'center' }}>
          <SearchBar onSearchSubmit={handleHomePageSearch} className="home-page-search-form" />
        </div>

        {/* Updated Favourites Section */}
        <div className="favourites-section">
          <h3>My Favourites</h3>
          {loadingFavourites && <p>Loading favourites...</p>}

          {!loadingFavourites && !currentUser && (
            <p className="favourites-placeholder">
              Please <Link to="/">log in</Link> to see your favourites.
            </p>
          )}

          {!loadingFavourites && currentUser && favouritePodcasts.length === 0 && (
            <p className="favourites-placeholder">
              Your favourite podcasts will appear here.
            </p>
          )}

          {!loadingFavourites && favouritePodcasts.length > 0 && (
            // Assuming .results-grid and .podcast-card styles are available (e.g., from SearchResultsPage.css or a global CSS)
            // You might need to add these styles to HomePage.css or a shared CSS file if they are not globally available.
            <div className="results-grid home-favourites-grid"> {/* Added home-favourites-grid for specific styling if needed */}
              {favouritePodcasts.map((podcast) => (
                <div
                  key={podcast.firestoreDocId}
                  className="podcast-card-hit-area"
                  onClick={() => {
                    window.location.href = `/podcast/${podcast.id}`;
                  }}
                  tabIndex={0}
                  role="button"
                  style={{ cursor: 'pointer' }}
                >
                  <div className="podcast-card">
                    <img
                      src={podcast.image || 'https://via.placeholder.com/160?text=No+Image'}
                      alt={podcast.title}
                    />
                    <div>
                      <h3 title={podcast.title}>{podcast.title}</h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Continue Where You Left Off Section */}
        {continueEpisode && (
          <div className="continue-listening-section">
            <h3 className="continue-listening-title">Continue where you left</h3>
            <div className="continue-chip">
              <img src={continueEpisode.episode.image || 'https://via.placeholder.com/60'} alt={continueEpisode.episode.title} className="continue-chip-image" />
              <div className="continue-chip-info">
                <h4 title={continueEpisode.episode.title} className="continue-chip-podcast-title">{continueEpisode.episode.title}</h4>
                <p className="continue-chip-time">Left at {formatTime(continueEpisode.currentTime)}</p>
              </div>
              <button className="continue-chip-play-btn" onClick={handleContinuePlay} title="Resume Episode">
                <img src={require('../assets/play-icon.svg').default} alt="Play" />
              </button>
              <button className="continue-chip-close-btn" onClick={handleCloseContinue} title="Dismiss">
                ×
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default HomePage;